import {Link, useLocation} from "react-router-dom";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import React from "react";
import List from "@material-ui/core/List";
import {useTranslation} from "react-i18next";
import {makeStyles} from "@material-ui/core/styles";
import * as _ from 'lodash';
import {useCapability} from "../../auth/CapabilityContext";
import {useOrganization} from "../../components/organizations/organization-context";
import {Roles} from "../../auth/roles";
import {Domains, MembershipCapabilityLevel, ProGuestCapabilityType, UserCapability} from "../../models/capabilities";
import isAfter from 'date-fns/isAfter';
import config from '../../config';
import {useMediaQuery, useTheme} from "@material-ui/core";

const useStyles = makeStyles(theme => ({
    root: {
        '& .Mui-selected': {
            backgroundColor: 'white',
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            borderLeftColor: theme.palette.primary.main,
        }
    },
    smallLogo: {
        maxWidth: 24
    }
}));

interface ISection {
    link: string,
    title: string,
    showSection: (organizationId: number | undefined, permissions: UserCapability | undefined, hasRole: (role: string) => boolean) => boolean,
}

export const sections: ISection[] = [
    {
        link: '/organizations',
        title: 'Organizations',
        showSection: (organizationId, permissions, hasRole) => !organizationId || hasRole(Roles.ADMIN) || _.size(_.values(permissions)) > 0,
    },
    {
        link: '/organization-units',
        title: 'Subscriptions',
        showSection: (organizationId, permissions, hasRole: any) => !!organizationId && (hasRole(Roles.ADMIN) || _.size(_.get(permissions, Domains.Membership))),
    },
    {
        link: '/machines',
        title: 'Machines',
        showSection: (organizationId, permissions, hasRole) => !organizationId || hasRole(Roles.ADMIN) || !!_.get(permissions, Domains.Machines),
    },
    {
        link: '/members',
        title: 'Members',
        showSection: (organizationId, permissions, hasRole) => !organizationId || hasRole(Roles.ADMIN) || _.size(_.get(permissions, Domains.Membership)) > 0,
    },
    {
        link: '/admins',
        title: 'Admins',
        showSection: (organizationId, permissions, hasRole) => !!organizationId && (hasRole(Roles.ADMIN) || _.size(_.get(permissions, Domains.Membership)) > 0),
    },
    {
        link: '/pro-guests',
        title: 'ProGuest',
        showSection: (organizationId, permissions, hasRole) => !!organizationId && (hasRole(Roles.ADMIN) || _.includes(_.get(permissions, Domains.ProGuest), ProGuestCapabilityType.Admin)),
    },
    {
        link: '/my-guests',
        title: 'MyGuests',
        showSection: (organizationId, permissions, hasRole) => !!organizationId && _.includes(_.get(permissions, Domains.ProGuest), ProGuestCapabilityType.Provider),
    },
    {
        link: '/usage-stats',
        title: 'UsageStats',
        showSection: (organizationId, permissions, hasRole) => !!organizationId && (
            hasRole(Roles.ADMIN)
            || (
                // Show feature only after planned toggle date for non IIT Admins
                isAfter(new Date(), config.usageStats.rolloutDate) &&
                _.find(permissions?.membership, p => p.level === 0)?.capabilities === MembershipCapabilityLevel.full
            )
        ),
    },
    {
        link: '/subscriptions-expiration-report',
        title: 'SubscriptionsExpirationReport',
        showSection: (organizationId, permissions, hasRole) => hasRole(Roles.ADMIN),
    },
    {
        link: '/device-activation-report',
        title: 'DeviceActivationReport',
        showSection: (organizationId, permissions, hasRole) => hasRole(Roles.ADMIN)
    },
    {
        link: '/merge-customers',
        title: 'MergeCustomers',
        showSection: (organizationId, permissions, hasRole) => hasRole(Roles.ADMIN)
    }
];

const buildSectionTemplate = (section: ISection, translate: any, sectionClicked: any, isSelected: boolean) => {
    return (
        <Link to={section.link}
              key={`section-${section.link}`}
              onClick={sectionClicked}>
            <ListItem button selected={isSelected}>
                <ListItemText style={{whiteSpace: 'normal'}}
                              primary={translate(`_Common.Section.${section.title}`)}/>
            </ListItem>
        </Link>
    );
};

interface SectionsListProps {
    closeDrawerClicked: () => void,
}

export const SectionsList: React.FC<SectionsListProps> = ({closeDrawerClicked}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const {capabilities, hasRole} = useCapability();
    const [organization] = useOrganization();
    const location = useLocation();
    const displayOrganizationsMenuItem = (capabilitiesToDisplay: any, hasRoleFn: any) =>
        hasRoleFn(Roles.ADMIN) || _.size(_.keys(capabilitiesToDisplay)) !== 1;
    const isSelectedPath = (section: any) => location.pathname.startsWith(section.link);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <List className={classes.root}>
            {
                sections
                    .filter(section => !section.link.includes('organizations') || displayOrganizationsMenuItem(capabilities, hasRole))
                    .filter(s => s.showSection(organization?.id, organization && capabilities[organization.id], hasRole))
                    .map(section => buildSectionTemplate(section, t, isMobile ? closeDrawerClicked : null, isSelectedPath(section)))
            }
        </List>
    );
}
